/**
 * Main Application Router
 * Role-based routing for 4 user interfaces
 */
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Student Interface
import StudentDashboard from './pages/student/Dashboard';
import TrainingDetail from './pages/student/TrainingDetail';
import LabSession from './pages/student/LabSession';
import StudentProfile from './pages/student/Profile';

// Facilitator Interface
import FacilitatorDashboard from './pages/facilitator/Dashboard';
import ClassroomManagement from './pages/facilitator/ClassroomManagement';
import SessionMonitor from './pages/facilitator/SessionMonitor';
import EvaluationReview from './pages/facilitator/EvaluationReview';
import FacilitatorProfile from './pages/facilitator/Profile';

// Admin Interface
import AdminDashboard from './pages/admin/Dashboard';
import TrainingManagement from './pages/admin/TrainingManagement';
import LabConfiguration from './pages/admin/LabConfiguration';
import UserManagement from './pages/admin/UserManagement';
import AdminProfile from './pages/admin/Profile';
import StudentAssignments from './pages/admin/StudentAssignments';
import AdminClassroomManagement from './pages/admin/ClassroomManagement';
import Analytics from './pages/admin/Analytics';

// Super Admin Interface
import SuperAdminDashboard from './pages/super-admin/Dashboard';
import SystemConfiguration from './pages/super-admin/SystemConfiguration';
import Reports from './pages/super-admin/Reports';
import SuperAdminProfile from './pages/super-admin/Profile';
import PlatformMetrics from './pages/super-admin/PlatformMetrics';

// Public
import Login from './pages/Login';

function LoadingSpinner() {
  return (
    <div style={{display:'flex', justifyContent:'center', alignItems:'center', height:'100vh'}}>
      <div>Loading...</div>
    </div>
  );
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
}

function RoleBasedRedirect() {
  const { user, loading } = useAuth();

  if (loading || !user) {
    return <LoadingSpinner />;
  }

  switch (user.role) {
    case 'student':
      return <Navigate to="/student/dashboard" />;
    case 'facilitator':
      return <Navigate to="/facilitator/dashboard" />;
    case 'admin':
      return <Navigate to="/admin/dashboard" />;
    case 'super_admin':
      return <Navigate to="/super-admin/dashboard" />;
    default:
      return <Navigate to="/login" />;
  }
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />

        {/* Role-based Default */}
        <Route path="/" element={<ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>} />

        {/* Student Routes */}
        <Route path="/student/dashboard" element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        } />
        <Route path="/student/training/:trainingId" element={
          <ProtectedRoute allowedRoles={['student']}>
            <TrainingDetail />
          </ProtectedRoute>
        } />
        <Route path="/student/lab/:labId" element={
          <ProtectedRoute allowedRoles={['student']}>
            <LabSession />
          </ProtectedRoute>
        } />
        <Route path="/student/profile" element={
          <ProtectedRoute allowedRoles={['student']}>
            <StudentProfile />
          </ProtectedRoute>
        } />

        {/* Facilitator Routes */}
        <Route path="/facilitator/dashboard" element={
          <ProtectedRoute allowedRoles={['facilitator', 'admin', 'super_admin']}>
            <FacilitatorDashboard />
          </ProtectedRoute>
        } />
        <Route path="/facilitator/classrooms" element={
          <ProtectedRoute allowedRoles={['facilitator', 'admin', 'super_admin']}>
            <ClassroomManagement />
          </ProtectedRoute>
        } />
        <Route path="/facilitator/sessions" element={
          <ProtectedRoute allowedRoles={['facilitator', 'admin', 'super_admin']}>
            <SessionMonitor />
          </ProtectedRoute>
        } />
        <Route path="/facilitator/sessions/:sessionId" element={
          <ProtectedRoute allowedRoles={['facilitator', 'admin', 'super_admin']}>
            <SessionMonitor />
          </ProtectedRoute>
        } />
        <Route path="/facilitator/evaluations" element={
          <ProtectedRoute allowedRoles={['facilitator', 'admin', 'super_admin']}>
            <EvaluationReview />
          </ProtectedRoute>
        } />
        <Route path="/facilitator/profile" element={
          <ProtectedRoute allowedRoles={['facilitator', 'admin', 'super_admin']}>
            <FacilitatorProfile />
          </ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin/dashboard" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/admin/trainings" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <TrainingManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/lab-config/:labId" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <LabConfiguration />
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/profile" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AdminProfile />
          </ProtectedRoute>
        } />
        <Route path="/admin/assignments" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <StudentAssignments />
          </ProtectedRoute>
        } />
        <Route path="/admin/classrooms" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
            <AdminClassroomManagement />
          </ProtectedRoute>
        } />
        <Route path="/admin/analytics/:trainingId" element={
          <ProtectedRoute allowedRoles={['admin', 'super_admin', 'facilitator']}>
            <Analytics />
          </ProtectedRoute>
        } />

        {/* Super Admin Routes */}
        <Route path="/super-admin/dashboard" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminDashboard />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/system" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SystemConfiguration />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/reports" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/profile" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <SuperAdminProfile />
          </ProtectedRoute>
        } />
        <Route path="/super-admin/metrics" element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <PlatformMetrics />
          </ProtectedRoute>
        } />
      </Routes>
    </BrowserRouter>
  );
}
